/**
 * RetryManager Service
 * Управляет повторными попытками API вызовов с exponential backoff
 * и предотвращает бесконечные циклы запросов
 */

class RetryManager {
  constructor() {
    // Отслеживаем текущие запросы по уникальному ключу
    this.activeRequests = new Map(); // key -> { count, timestamp, timeoutId }
    this.retryDelays = new Map(); // key -> delay в ms
  }

  /**
   * Проверить, может ли запрос быть повторен
   * @param {string} requestKey - Уникальный ключ запроса (например, "transactions_filter_page_1")
   * @param {Object} options - { maxRetries: 3, backoffMultiplier: 2, baseDelay: 1000 }
   * @returns {Object} { canRetry: boolean, delay: number, retryCount: number }
   */
  static canRetry(requestKey, options = {}) {
    const {
      maxRetries = 3,
      backoffMultiplier = 2,
      baseDelay = 1000,
    } = options;

    const manager = this._getInstance();
    const current = manager.activeRequests.get(requestKey);

    // First request attempt - no entry in tracking map yet
    if (!current) {
      return {
        canRetry: true,
        delay: 0,
        retryCount: 0,
        reason: null
      };
    }

    // Если уже было максимальное количество попыток
    if (current.count >= maxRetries) {
      return {
        canRetry: false,
        delay: 0,
        retryCount: current.count,
        reason: `Max retries (${maxRetries}) exceeded`
      };
    }

    // Если запрос слишком недавно (менее 100ms назад), не повторяем
    const timeSinceLastAttempt = Date.now() - current.timestamp;
    if (timeSinceLastAttempt < 100) {
      return {
        canRetry: false,
        delay: 0,
        retryCount: current.count,
        reason: 'Request already in progress'
      };
    }

    // Exponential backoff: delay = baseDelay * (backoffMultiplier ^ count)
    const delay = baseDelay * Math.pow(backoffMultiplier, current.count);

    return {
      canRetry: true,
      delay,
      retryCount: current.count,
      reason: null
    };
  }

  /**
   * Регистрировать попытку запроса
   * @param {string} requestKey - Уникальный ключ запроса
   */
  static recordAttempt(requestKey) {
    const manager = this._getInstance();
    const current = manager.activeRequests.get(requestKey) || { count: 0 };

    manager.activeRequests.set(requestKey, {
      count: current.count + 1,
      timestamp: Date.now()
    });
  }

  /**
   * Очистить информацию о запросе при успехе
   * @param {string} requestKey - Уникальный ключ запроса
   */
  static clearRequest(requestKey) {
    const manager = this._getInstance();
    manager.activeRequests.delete(requestKey);
  }

  /**
   * Проверить, находится ли запрос в состоянии retry
   * @param {string} requestKey - Уникальный ключ запроса
   * @returns {boolean}
   */
  static isRetrying(requestKey) {
    const manager = this._getInstance();
    const current = manager.activeRequests.get(requestKey);
    return current && current.count > 0;
  }

  /**
   * Получить количество попыток для запроса
   * @param {string} requestKey - Уникальный ключ запроса
   * @returns {number}
   */
  static getRetryCount(requestKey) {
    const manager = this._getInstance();
    const current = manager.activeRequests.get(requestKey);
    return current ? current.count : 0;
  }

  /**
   * Debounce функция с максимальным timeout
   * @param {Function} func - Функция для debounce
   * @param {number} delay - Delay в ms
   * @param {number} maxWait - Максимальный timeout в ms
   * @returns {Function}
   */
  static debounce(func, delay = 300, maxWait = 5000) {
    let timeoutId;
    let maxTimeoutId;
    let lastCallTime;
    let lastResult;

    return function debounced(...args) {
      const now = Date.now();

      // Если прошло больше maxWait с первого вызова, выполнить сразу
      if (lastCallTime && now - lastCallTime > maxWait) {
        clearTimeout(timeoutId);
        if (maxTimeoutId) clearTimeout(maxTimeoutId);
        lastResult = func.apply(this, args);
        lastCallTime = now;
        return lastResult;
      }

      lastCallTime = now;

      // Отменить предыдущий timeout
      clearTimeout(timeoutId);

      // Установить новый timeout
      timeoutId = setTimeout(() => {
        func.apply(this, args);
      }, delay);

      return lastResult;
    };
  }

  /**
   * Throttle функция - вызывать максимум раз в delay ms
   * @param {Function} func - Функция для throttle
   * @param {number} delay - Delay в ms
   * @returns {Function}
   */
  static throttle(func, delay = 300) {
    let lastCallTime = 0;
    let timeoutId;

    return function throttled(...args) {
      const now = Date.now();

      if (now - lastCallTime >= delay) {
        clearTimeout(timeoutId);
        lastCallTime = now;
        func.apply(this, args);
      } else if (!timeoutId) {
        // Если не вызывали в течение delay, запланировать для конца периода
        timeoutId = setTimeout(() => {
          lastCallTime = Date.now();
          func.apply(this, args);
          timeoutId = null;
        }, delay - (now - lastCallTime));
      }
    };
  }

  /**
   * Безопасно выполнить функцию с retry логикой
   * @param {Function} fn - Async функция для выполнения
   * @param {string} requestKey - Уникальный ключ запроса
   * @param {Object} options - Опции retry
   * @returns {Promise}
   */
  static async executeWithRetry(fn, requestKey, options = {}) {
    const retryInfo = this.canRetry(requestKey, options);

    if (!retryInfo.canRetry) {
      throw new Error(retryInfo.reason || 'Max retries exceeded');
    }

    this.recordAttempt(requestKey);

    try {
      const result = await fn();
      this.clearRequest(requestKey);
      return result;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Получить singleton instance
   * @private
   */
  static _getInstance() {
    if (!this._instance) {
      this._instance = new RetryManager();
    }
    return this._instance;
  }

  /**
   * Сбросить все отслеживания (для очистки при unmount)
   */
  static resetAll() {
    const manager = this._getInstance();
    manager.activeRequests.clear();
    manager.retryDelays.clear();
  }
}

export default RetryManager;



// WEBPACK FOOTER //
// ./src/Services/RetryManager.js